import { gql } from '@apollo/client';
import {
  AllSubscriptionStatus,
  EdgesType,
  IGadCountry,
  IPaybPayment,
  IProduct,
  IProductParent,
  PaybBillingPlan,
  SubscriptionSiteId,
} from '@global/types/types';

export const GET_SUBSCRIPTION = gql`
  query wGetSubscriptions($userUid: String!, $statuses: [String!]) {
    wGetSubscriptions(userUid: $userUid, statuses: $statuses)
  }
`;

export interface GET_SUBSCRIPTION_VARIABLES {
  userUid: string;
  statuses: AllSubscriptionStatus[];
}

type Parent = {
  node: Pick<IProductParent['node'], 'maName' | 'wImages'>;
};

type BiiSalable = Pick<IProduct, 'maId' | 'maName' | 'imageUrl'> & {
  parents: Parent[];
};

type HiConsistsOf = {
  biiSalable: BiiSalable;
  biiQuantity: { amount: number };
};

type PaymAmount = {
  edges: EdgesType[];
};

type PaybPayment = IPaybPayment & {
  uid: string;
  paymAmount: PaymAmount;
};

type Address = {
  upaLine1: string;
  upaLine2: string;
  upaCity: string | null;
  gadStateOrProvince: string;
  wCountry: string | null;
  gadCountry: Pick<IGadCountry, 'name' | 'numericCode' | 'iso2Code'>;
  upaPostcode: string;
};

type SubsValidPeriod = {
  start: string;
  end: string;
};

type PaContact = {
  typeName: string;
  email: string;
};

type PaContactEdge = {
  node: PaContact;
};

type PaContacts = {
  edges: PaContactEdge[];
};

type PrRelatedParties = {
  edges: {
    node: { paId: string; paContacts: PaContacts };
  }[];
};

interface ViaxCustomerOrder {
  typeName: string;
  uid: string;
  biId: string;
  wSiteId: SubscriptionSiteId;
}

interface OrderEdge {
  node: ViaxCustomerOrder;
}

interface HiPartOfBase {
  order?: {
    edges: OrderEdge[];
  };
  uid?: string;
  biId?: string;
  wSiteId?: SubscriptionSiteId;
}

type HiPartOf = {
  typeName: string;
  uid: string;
  biiId: string;
  paybBillingPlan?: PaybBillingPlan;
  hiPartOf: (HiPartOf & HiPartOfBase)[];
};

export interface SubscriptionNode {
  uid: string;
  bpStatus: { code: AllSubscriptionStatus };
  hiConsistsOf: HiConsistsOf[];
  hiPartOf: HiPartOf[];
  paybPayment: PaybPayment;
  wContactPhone: string | null;
  paybBillingAddress: Address;
  subsValidPeriod: SubsValidPeriod;
  paybNextBillDate: string | null;
  prRelatedParties: PrRelatedParties;
  paybBillingPlan: PaybBillingPlan;
  lastBillBiId: string;
}

interface Edge {
  node: SubscriptionNode;
}

interface SubscriptionsDetail {
  edges: Edge[];
}

interface ErrorData {
  code?: string;
  message: string;
}

interface SuccessResult {
  status: 'OK';
  data: SubscriptionsDetail;
}

interface ErrorResult {
  status: 'ERROR';
  errors: ErrorData;
}

export interface GET_SUBSCRIPTION_RESPONSE {
  wGetSubscriptions: {
    result: SuccessResult | ErrorResult;
  };
}
